import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Loader } from 'components';
import {
  ApiError,
  AwsPicture,
  DepositAction,
  UpdateClassifiedRequest,
  UpdateDepositContext as UpdateDepositContextType,
  UpdateDepositState,
  UpdateDepositValues,
  User,
  VehicleType,
} from 'types';
import { config } from 'config';
import { ClassifiedManagementService } from 'api/Classifieds/ClassifiedManagementService';
import { ACTIONS, verticals } from 'utils';
import { VehicleDescriptionService, UserManagementService, DepositPicturesService } from 'api';
import { EquipmentOrOption, Vehicle } from '@groupelacentrale/classified-core';
import { formatDateReverse } from 'utils/date';
import { VEHICLE_REPAIRS } from 'utils/vehicleRepairs';
import { LabeloService } from 'api/Dab/LabeloService';
import { Classified } from '@groupelacentrale/classified-core';
import { useMountEffect } from 'hooks/use-mount-effect';
import { allowDeposit } from 'utils/allowDeposit';

export const UpdateDepositContext = React.createContext<UpdateDepositContextType>({
  classified: undefined,
  userData: undefined,
  updateClassified: Promise.resolve,
  setUpdateValues: () => {},
  setPhoneRequestId: () => {},
  renewClassified: Promise.resolve,
  upsellClassified: Promise.resolve,
  uploadPhotos: Promise.resolve,
  handleReturn: Promise.resolve,
});

interface Props {
  user: User;
  action: DepositAction;
  reference: string;
}

const fetchVehicle = async (vehicleType: VehicleType, vehicle: Vehicle) => {
  const { versionId, category, firstCirculationDate, equipmentsOrOptions, repairs } = vehicle;
  const newRepairs = VEHICLE_REPAIRS[vehicleType].map(r => ({
    label: r,
    selected: repairs && repairs.includes(r),
  }));
  const newVehicleDescription = (await VehicleDescriptionService.getVehicleEquipmentsOptionsPacks(
    versionId,
    category!,
    formatDateReverse(new Date(firstCirculationDate!)),
    false,
    equipmentsOrOptions?.map((item: EquipmentOrOption) => item.label),
  )) ?? {
    equipments: equipmentsOrOptions?.map((item: EquipmentOrOption) => ({ label: item.label, selected: true })),
    options: vehicle.options?.map((item: string) => ({ label: item, selected: true })),
    packs: vehicle.packs?.map((item: string) => ({ label: item, selected: true })),
  };

  return { ...vehicle, repairs: newRepairs, ...newVehicleDescription };
};

export const UpdateDepositProvider: React.FC<Props> = ({ action, reference, user, children }) => {
  const [loading, setLoading] = useState(true);
  const [depositUpdate, setDepositUpdate] = useState<UpdateDepositState>();

  useMountEffect(() => {
    localStorage.setItem('classifiedReference', reference);
    (async () => {
      try {
        const result = await ClassifiedManagementService.getClassified(reference);
        const type = verticals[result.vehicle.category];
        if (allowDeposit(type)) {
          localStorage.setItem('depositType', type);
          const isUpdate = ACTIONS[action as keyof typeof ACTIONS] === 'update';
          const vehicle = isUpdate ? await fetchVehicle(type, result.vehicle) : result.vehicle;
          const classified = { ...result, vehicle };
          const labelos = user.isPro && isUpdate ? await LabeloService.getLabelos(user.username) : undefined;
          const pictures =
            classified.picturesData?.countValid && isUpdate
              ? await DepositPicturesService.getDepositPictures(
                  classified.reference!,
                  classified.picturesData.countValid,
                  classified.lastUpdate!,
                )
              : [];
          const userData = user.isPro
            ? await ClassifiedManagementService.getProContactInfo(user.username)
            : await UserManagementService.getUser(user.id);

          setDepositUpdate({ classified, type, labelos, userData, pictures });
          setLoading(false);
        } else if (type === undefined) {
          setDepositUpdate({ error: 'Categorie non trouvée' });
          setLoading(false);
        } else {
          window.location.href =
            config.URL_LEGACY_DEPOSIT + `classified/${reference}/${ACTIONS[action as keyof typeof ACTIONS]}`;
        }
      } catch (r) {
        setDepositUpdate({ error: (r as ApiError).message });
        setLoading(false);
      }
    })();
  });

  const updateClassified = useCallback((values: UpdateClassifiedRequest) => {
    return ClassifiedManagementService.updateClassified(values).then(classified => {
      setDepositUpdate(prevState => ({ ...prevState, classified, updated: true }));
    });
  }, []);

  const setUpdateValues = useCallback((values: UpdateDepositValues) => {
    setDepositUpdate(prevState => ({ ...prevState, updateValues: values }));
  }, []);

  const setPhoneRequestId = useCallback((requestId: string) => {
    setDepositUpdate(prevState => ({ ...prevState, phoneRequestId: requestId }));
  }, []);

  const renewClassified = useCallback((isSecuritel: boolean, redirect: boolean) => {
    return ClassifiedManagementService.renewClassified(isSecuritel).then(classified =>
      setDepositUpdate(prevState => ({ ...prevState, classified, updated: redirect })),
    );
  }, []);

  const upsellClassified = useCallback((isSecuritel: boolean, redirect?: boolean) => {
    return ClassifiedManagementService.updateClassified({ isSecuritel }).then((classified: Classified) =>
      setDepositUpdate(prevState => ({ ...prevState, classified, updated: redirect })),
    );
  }, []);

  const uploadPhotos = useCallback(async (classified: Classified, awsPictures: AwsPicture[]) => {
    if (awsPictures) {
      await ClassifiedManagementService.uploadPhoto(classified, awsPictures, 'VO').catch(() =>
        alert("Erreur lors de l'upload des photos"),
      );
    }
  }, []);

  const handleReturn = useCallback(() => {
    if (user!.isPro) {
      window.location.href = config.URL_PILOT + 'annonces';
    } else {
      window.location.href = config.URL_LACENTRALE + 'compte/annonces';
    }
  }, [user]);

  const contextValue = useMemo(
    (): UpdateDepositContextType => ({
      ...depositUpdate,
      updateClassified,
      setUpdateValues,
      setPhoneRequestId,
      renewClassified,
      upsellClassified,
      uploadPhotos,
      handleReturn,
    }),
    [
      depositUpdate,
      updateClassified,
      setUpdateValues,
      setPhoneRequestId,
      renewClassified,
      upsellClassified,
      uploadPhotos,
      handleReturn,
    ],
  );

  return (
    <UpdateDepositContext.Provider value={contextValue}>
      {loading ? <Loader /> : children}
    </UpdateDepositContext.Provider>
  );
};

export const useUpdateDeposit = (): UpdateDepositContextType => useContext(UpdateDepositContext);
